import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-hover shadow-md hover:shadow-lg hover:-translate-y-0.5",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent/10 hover:text-accent-foreground hover:border-accent/50 hover:-translate-y-0.5",
        secondary: "bg-secondary text-secondary-foreground hover:bg-muted hover:-translate-y-0.5",
        ghost: "hover:bg-accent/10 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-gradient-to-r from-teen-green to-teen-blue text-white font-bold hover:from-teen-green/90 hover:to-teen-blue/90 shadow-teen-glow hover:shadow-lg transform hover:scale-105 transition-all duration-300",
        hatch: "bg-gradient-hatch text-white hover:shadow-hatch animate-hatch-glow font-semibold",
        bot: "bg-gradient-to-r from-secondary to-muted text-foreground hover:from-accent/20 hover:to-accent/10 border border-border hover:border-accent/50",
        game: "bg-card text-card-foreground border-2 border-primary/30 hover:border-primary hover:shadow-vibrant transform hover:scale-105",
        vibrant: "bg-gradient-to-r from-teen-pink via-teen-purple to-teen-cyan text-white font-semibold hover:from-teen-pink/90 hover:via-teen-purple/90 hover:to-teen-cyan/90 shadow-lg shadow-teen-purple/25 hover:shadow-teen-purple/40 hover:-translate-y-0.5",
        glow: "bg-primary text-primary-foreground shadow-[0_0_20px_hsl(var(--primary)/0.4)] hover:shadow-[0_0_30px_hsl(var(--primary)/0.6)] hover:-translate-y-0.5",
        glass: "bg-white/10 backdrop-blur-md border border-white/20 text-foreground hover:bg-white/20 hover:border-white/30",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-sm px-3 text-xs",
        lg: "h-12 rounded-md px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
